<template>
  <dashboard-chart-card
    title="Resumen de interacciones por canal de mensajería"
    @handleDownloadClick="$emit('handleDownload')"
  >
    <template #info>
      <a-popover
        trigger="hover"
        class="popover"
        placement="bottomRight"
        :overlayStyle="{ width: '300px' }"
      >
        <template #title>
          <span class="titlePopover">Interacciones por canales de mensajería</span></template
        >
        <template #content>
          <div class="content">
            <span>
              Es el número de veces que responde o envía un mensaje el contacto a la empresa. Son
              contabilizados por hora o día, según el rango de tiempo que se aplique al filtro.
            </span>
            <p class="separator"></p>
            <span class="subtitle">Ejemplo por hora:</span>
            <span>
              Si el contacto envía varios mensajes en el rango de una hora, se considera una sola
              interacción. Es decir, si el contacto ha enviado mensajes en un rango de 3 horas, se
              consideran 3 interacciones.
            </span>
            <p class="separator"></p>
            <span class="subtitle"> Ejemplo por día:</span>
            <span>
              Si el contacto envía varios mensajes en el rango de un día (independientemente de la
              hora), se considera una sola interacción</span
            >
          </div>
        </template>
        <InfoCircleOutlined class="info-circle" />
      </a-popover>
    </template>
    <div class="section-graph">
      <apexcharts height="250" type="line" :options="chartOptions" :series="series"></apexcharts>
      <div class="title">
        <a-typography-text strong>Total de interacciones</a-typography-text>
        <div class="grid-cards">
          <interaction-card
            v-for="(item, index) in dataSourceInteractions"
            :key="`ìnteraction-message-${index}`"
            v-bind="item"
          />
        </div>
      </div>
    </div>
  </dashboard-chart-card>
</template>

<script>
import { defineComponent } from "vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import VueApexCharts from "vue3-apexcharts";
import _ from "lodash";
import dayjs from "dayjs";

import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { ChannelType } from "@/app/shared/utils/enums";

// import IconChannel from "@/app/shared/components/icons/IconChannel";
import DashboardChartCard from "../cards/DashboardChartCard.vue";
import InteractionCard from "@/app/dashboard/components/cards/InteractionCard.vue";

export default defineComponent({
  components: {
    DashboardChartCard,
    InfoCircleOutlined,
    // IconChannel,
    apexcharts: VueApexCharts,
    InteractionCard,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    hourRange: {
      required: true,
      type: Object,
    },
  },
  setup() {
    return { ChannelType };
  },
  methods: {
    getSeriesByChannel(channel_name) {
      const array = this.rawInteractionsData?.filter((val) => val.channel == channel_name);
      const serie = [];
      this.interactionsCategories.forEach((item) => {
        const obj = array.find((x) => (x.date ? x.date == item : x.hour == item));
        if (obj) serie.push(obj.count);
        else serie.push(0);
      });
      return serie;
    },
  },
  computed: {
    rawInteractionsData() {
      const interactions = this.data?.interactions;
      const hourRangeArray = Object.keys(this.hourRange).map((hour) => Number(hour));

      if (hourRangeArray.length !== 0) {
        const formattedInteractionsData = hourRangeArray.flatMap((hour) => [
          { hour, count: 0, channel: "WhatsApp Message" },
          { hour, count: 0, channel: "Webchat" },
          { hour, count: 0, channel: "Messenger" },
          { hour, count: 0, channel: "Instagram" },
        ]);

        interactions?.forEach((interaction) => {
          const interactionHour = dayjs(interaction?.date).hour();

          const idx = formattedInteractionsData.findIndex(
            ({ hour, channel }) => interactionHour === hour && interaction?.channel === channel
          );

          const { channel, count } = interaction;
          const newInteraction = { hour: interactionHour, channel, count };

          formattedInteractionsData[idx] = newInteraction;
        });

        return formattedInteractionsData;
      }

      return interactions;
    },
    dataSourceInteractions() {
      return [
        {
          channel: ChannelType.Whatsapp,
          number: this.whatsData,
          title: "WhatsApp",
          color: "green",
        },
        {
          channel: ChannelType.Inbound,
          number: this.webData,
          title: "Web Chat",
          color: "volcano",
        },
        {
          channel: ChannelType.Messenger,
          number: this.messengerData,
          title: "Messenger",
          color: "purple",
        },
        {
          channel: ChannelType.Instagram,
          number: this.instagramData,
          title: "Instagram",
          color: "magenta",
        },
      ];
    },
    whatsData() {
      const pro = this.data?.channels.find((x) => x.channel == "WhatsApp Message");
      if (pro) {
        return pro.count;
      } else {
        return 0;
      }
    },
    webData() {
      const pro = this.data?.channels.find((x) => x.channel == "Webchat");
      if (pro) {
        return pro.count;
      } else {
        return 0;
      }
    },
    messengerData() {
      const pro = this.data?.channels.find((x) => x.channel == "Messenger");
      if (pro) {
        return pro.count;
      } else {
        return 0;
      }
    },
    instagramData() {
      const pro = this.data?.channels.find((x) => x.channel == "Instagram");
      if (pro) {
        return pro.count;
      } else {
        return 0;
      }
    },
    serieWhats() {
      return this.getSeriesByChannel("WhatsApp Message");
    },
    serieWeb() {
      return this.getSeriesByChannel("Webchat");
    },
    serieMessenger() {
      return this.getSeriesByChannel("Messenger");
    },
    serieInstagram() {
      return this.getSeriesByChannel("Instagram");
    },
    series() {
      return [
        {
          name: "WhatsApp",
          data: this.serieWhats,
        },
        {
          name: "Web Chat",
          data: this.serieWeb,
        },
        {
          name: "Messenger",
          data: this.serieMessenger,
        },
        {
          name: "Instagram",
          data: this.serieInstagram,
        },
      ];
    },
    chartOptions() {
      return {
        chart: {
          type: "line",
          height: 250,
          zoom: {
            autoScaleYaxis: true,
          },
          toolbar: {
            show: false,
          },
        },
        stroke: {
          width: 2,
          curve: "straight",
        },
        colors: ["#B7EB8F", "#FFBB96", "#D3ADF7", "#FFADD2"],
        dataLabels: {
          enabled: false,
        },
        markers: {
          size: 0,
          style: "hollow",
        },
        xaxis: {
          categories: this.interactionsCategoriesLabel,
        },
        tooltip: {
          x: {
            format: "dd MM",
          },
          y: {
            formatter: (val) => val,
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
      };
    },
    interactions() {
      return _.sortBy(this.rawInteractionsData, ["date", "hour", "count"]);
    },
    interactionsCategories() {
      return _.sortedUniq(this.interactions.map((value) => (value.date ? value.date : value.hour)));
    },
    interactionsCategoriesLabel() {
      return _.sortedUniq(
        this.interactions.map((value) =>
          value.date ? dateTimeToSlashFormat(value.date, false) : value.hour + ":00hrs"
        )
      );
    },
  },
});
</script>

<style lang="sass" scoped>
.popover
  margin-right: 20px
.info-circle
  font-size: 14px
  color: #51555A
.content
  font-weight: 300
  color: $gray-8
.title
  text-align: center
  height: 95%
  width: 19vw
  & > div
    padding: 10px
.section-graph
  display: grid
  grid-template-columns: 1.6fr 0.4fr
  gap: 8px
.grid-cards
  display: grid
  grid-template-columns: 1fr 1fr
  grid-template-rows: 1fr 1fr
  gap: 8px
  height: 80%

.titlePopover
  font-size: 16px
  font-weight: 600

.separator
  margin-top: 2px
  margin-bottom: 2px

.subtitle
  color: $gray-8
  font-weight: 600
</style>
